import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody, TextField } from '@mui/material';
import DraggableDialog from '../components/common/DraggableDialog';
import apiService from '../services/api.service';

interface CompanyRow { CompanyID: number; CompanyName: string; }
interface ScheduleRow { scheduleid: number; name: string; companyid: number | null; endpointkey: string; selectedfields: string[]; datefield?: string | null; lastruntime?: string | null; }

const LocalItemMaster: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [scheduleId, setScheduleId] = useState<number | ''>('');
  const [rows, setRows] = useState<any[]>([]);
  const [editRow, setEditRow] = useState<any | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});

  const schedule = useMemo(() => schedules.find(s => s.scheduleid === scheduleId), [schedules, scheduleId]);
  const fields = schedule?.selectedfields || [];

  useEffect(() => {
    const init = async () => {
      const companiesRes = await apiService.getCompanies(true);
      const rawCompanies = Array.isArray(companiesRes) ? companiesRes : (companiesRes?.data || []);
      setCompanies(rawCompanies.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyName: c.CompanyName ?? c.companyname })));
      const schedulesRes = await apiService.listSchedules();
      setSchedules(schedulesRes.filter((s: any) => s.endpointkey === 'sap-items'));
    };
    init();
  }, []);

  const load = async () => {
    const result = await apiService.listLocalItemMaster(companyId === '' ? undefined : Number(companyId), fields);
    const payload = Array.isArray(result) ? result : (result?.data || []);
    setRows(payload);
  };

  const sync = async () => {
    if (!scheduleId) return;
    await apiService.syncLocalItemMaster(Number(scheduleId));
    await load();
  };

  const openEdit = (row: any) => {
    setEditRow(row);
    setEditData({ ...(row?.data || {}) });
  };
  const saveEdit = async () => {
    if (!editRow) return;
    await apiService.updateLocalItem(editRow.id, editData);
    setEditRow(null);
    await load();
  };

  const createRow = async () => {
    await apiService.createLocalItem(companyId === '' ? undefined : Number(companyId), editData['ItemCode'] || '', editData);
    setEditRow(null);
    await load();
  };

  return (
    <Box component="section" aria-labelledby="lim-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography
        component="h1"
        id="lim-heading"
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
      >
        Local Item Master
      </Typography>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="company-select">Company</InputLabel>
              <Select labelId="company-select" value={companyId} label="Company" onChange={(e) => setCompanyId(e.target.value as any)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {companies.map(c => (<MenuItem key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="schedule-select">Schedule (Items)</InputLabel>
              <Select labelId="schedule-select" value={scheduleId} label="Schedule (Items)" onChange={(e) => setScheduleId(Number(e.target.value))}>
                <MenuItem value=""><em>None</em></MenuItem>
                {schedules.map(s => (<MenuItem key={s.scheduleid} value={s.scheduleid}>{s.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Button variant="contained" onClick={load} sx={{ mr: 2 }}>Load</Button>
            <Button variant="outlined" onClick={sync} disabled={!scheduleId}>Sync from SAP</Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Button variant="contained" onClick={() => openEdit({ id: null, data: {} })} sx={{ mb: 2 }}>Create</Button>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ItemCode</TableCell>
              {fields.map(f => (<TableCell key={f}>{f}</TableCell>))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={idx}>
                <TableCell>{String(r.itemcode ?? '')}</TableCell>
                {fields.map(f => (<TableCell key={f}>{String((r.data || {})[f] ?? '')}</TableCell>))}
                <TableCell align="right">
                  <Button variant="outlined" size="small" onClick={() => openEdit(r)}>Edit</Button>
                  <Button variant="outlined" color="error" size="small" onClick={async () => { await apiService.deleteLocalItem(r.id); await load(); }} sx={{ ml: 1 }}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={Math.max(2, fields.length + 2)}>No data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <DraggableDialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="md" fullWidth title={editRow?.id ? 'Edit Item' : 'Create Item'} actions={
        <>
          {editRow?.id ? (
            <Button variant="contained" onClick={saveEdit}>Save</Button>
          ) : (
            <Button variant="contained" onClick={createRow}>Create</Button>
          )}
          <Button variant="outlined" onClick={() => setEditRow(null)}>Cancel</Button>
        </>
      }>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField label="ItemCode" fullWidth size="small" value={String(editData['ItemCode'] ?? '')} onChange={(e) => setEditData({ ...editData, ItemCode: e.target.value })} />
          </Grid>
          {fields.map(f => (
            <Grid item xs={12} md={3} key={f}>
              <TextField label={f} fullWidth size="small" value={String(editData[f] ?? '')} onChange={(e) => setEditData({ ...editData, [f]: e.target.value })} />
            </Grid>
          ))}
        </Grid>
      </DraggableDialog>
    </Box>
  );
};

export default LocalItemMaster;